import View from 'ol/View';
import Attribution from 'ol/control/Attribution';
import * as olProj from 'ol/proj';
import { defaults as defaultInteractions, PinchZoom } from 'ol/interaction';
import { Injectable, NgZone, inject } from '@angular/core';
import SidebarEsk, { SidebarEsk as Sidebar } from '../_helpers/sidebar_esk';
import LayerGroup from 'ol/layer/Group';
import { populateLayerGroup } from '../_helpers/LayerBuilder';
import { mapProjEPSG } from '../_helpers/projection_setup';
import EsriJSON from 'ol/format/EsriJSON';
import { createXYZ } from 'ol/tilegrid';
import { base_map_defs } from '../_helpers/basemaps';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { tile as tileStrategy } from 'ol/loadingstrategy';
import { Collection, Feature } from 'ol';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import {
  Buffer,
  ConvertFeatureToGeoJson,
  ConvertGeoJsonToFeatureCollection,
  ConvertFeatureCollectionToGeoJson,
  Union,
  Intersect,
  CloneProperties,
  CalculateArea,
  Difference,
  Intersects,
  ConvertFeatureToWKT,
  ConvertWktToFeature
} from '../_helpers/transformations';
import { LIST_MAP_DEFS, VERSATILITY_NAMES } from '../_helpers/otherMaps';
import { ClippedLayerService } from './BaseLayerServices/clippedLayer.service';
import {
  fireAlertStyleIcon,
  firePermitStyleIcon,
  getCadasterStyle,
  getFcCodeStyle,
  getFenceStyle,
  getPaddockStyle,
  getPropertiesStyle,
  getTopexStyle,
  getVersatilityStyle,
  styleMap
} from '../_helpers/layer.styles';
import { PropertyService } from './property.service';
import { AuthService } from './auth.service';
import { User } from '../models/user.model';
import { PaddockService } from './paddock.service';
import { FenceDexie } from '../models/fence.model';
import VectorImageLayer from 'ol/layer/VectorImage';
import { rankCrops } from '../_helpers/helperFunctions-esk';
import { EventBusService } from './EventBus.service';
import { FenceService } from './fence.service';
import { ProcessState } from 'src/app/_helpers/enums';
import { SpatialService } from './spatial.service';
import {
  ClippedLayer_Paddock,
  ClippedPropertyLayer
} from '../models/clippedLayers.model';
import { DexieDatabaseService } from './dexieIndexDB.service';
import { PropertyDexie } from '../models/property.model';

import { tap } from 'rxjs/operators';
import { PaddockDexie } from '../models/paddock.model';
import {
  EnterpriseReport,
  EnterpriseReportRow
} from '../models/enterpriseReport.model';
import { EnterpriseReportService } from './enterpriseReport.service';
import { EnterpriseService } from './enterprise.service';
import { Enterprise } from '../models/enterprise.model';
import { TopexReport } from '../models/topexReport.model';
import { TopexReportService } from './topexReport.service';
import { ForestDescriptionReport } from '../models/Forest Description/forestReport.model';
import { ForestClassReportService } from './forestClassesReport.service';
import { ExportableMap } from '../_helpers/ExportableMap';
import { PeatLayerDTO } from '../components/map/dialogs/pine-economic-analysis-tool/data-acces/peat-layers-dto.model';
import { SubregionPropertiesDTO } from '../components/map/dialogs/pine-economic-analysis-tool/data-acces/subregion-properties-dto.model';
import { PineEconomicAnalysisReport } from '../models/PEAT Tool/pine-economic-analysis-report.model';
import { PineEconomicAnalysisToolService } from './pine-economic-analysis-tool.service';
import { transform } from 'ol/proj';
import { ThinningAndHarvestYield } from '../models/PEAT Tool/thinning-and-harvest-yield.model';
import { PineEconomicAnalysisToolRow } from '../models/PEAT Tool/pine-economic-analysis-tool-row.model';
import { MultiPolygon, Polygon } from 'ol/geom';
import { Coordinate } from 'ol/coordinate';
import { PineEconomicAnalysisToolAccuYieldRow } from '../models/PEAT Tool/pine-economic-analysis-tool-accu-yield-row.model';

@Injectable({
  providedIn: 'root'
})
export class mapService {
  baseLayerService = inject(ClippedLayerService);
  propertyService = inject(PropertyService);
  private auth = inject(AuthService);
  private paddockService = inject(PaddockService);
  private eventBusService = inject(EventBusService);
  private fenceService = inject(FenceService);
  private spatialService = inject(SpatialService);
  private zone = inject(NgZone);
  private dexieService = inject(DexieDatabaseService);
  private enterpriseReportService = inject(EnterpriseReportService);
  private enterpriseService = inject(EnterpriseService);
  private topexReportService = inject(TopexReportService);
  private forestDescriptionReportService = inject(ForestClassReportService);
  private pineEconomicAnalysisReportService = inject(
    PineEconomicAnalysisToolService
  );

  ProgressDetail = new BehaviorSubject<string>(null);

  topexVectorSource_Property: VectorSource<any> = new VectorSource();
  topexVectorSource_Paddock: VectorSource<any> = new VectorSource();

  forestClassesVectorSource_Property: VectorSource<any> = new VectorSource();
  forestClassesVectorSource_Paddock: VectorSource<any> = new VectorSource();

  cartageVectorSource_Property: VectorSource<any> = new VectorSource();
  cartageVectorSource_Paddock: VectorSource<any> = new VectorSource();

  subregionVectorSource_Property: VectorSource<any> = new VectorSource();
  subregionVectorSource_Paddock: VectorSource<any> = new VectorSource();

  propertiesVectorSource: VectorSource<any> = new VectorSource();
  propertiesLayer: VectorLayer<any> = new VectorLayer({
    source: this.propertiesVectorSource,
    visible: false,
    style: (feature, resolution) => {
      return getPropertiesStyle(feature, resolution, '');
    }
  });

  ProcessingState = new BehaviorSubject<ProcessState>(
    ProcessState.Not_Processing
  );
  createPaddockFeature = new BehaviorSubject<Feature<any>>(null);

  map: ExportableMap;

  proj = olProj.get(mapProjEPSG);

  esrijsonFormat = new EsriJSON();

  propAliasMap: Map<string, string> = new Map();

  listLayerMap: Map<VectorSource<any>, VectorLayer<any>> = new Map();

  cadasterLayer: VectorImageLayer<any>;

  basemapGroup;

  layerSwitcher;

  propertyLayer = new VectorLayer({
    source: new VectorSource(),
    style: styleMap.get('Property')
  } as any);

  paddockLayer = new VectorLayer({
    source: new VectorSource({}),
    /* A function that returns a style object. */
    visible: true,
    style: (feature, resolution) => {
      return getPaddockStyle(feature, resolution, '');
    }
  });

  topexLayerProperty = new VectorLayer({
    source: this.topexVectorSource_Property,
    /* A function that returns a style object. */
    visible: false,
    style: (feature, resolution) => {
      return getTopexStyle(feature, resolution, '');
    }
  });

  topexLayerPaddock = new VectorLayer({
    source: this.topexVectorSource_Paddock,
    /* A function that returns a style object. */
    visible: false,
    style: (feature, resolution) => {
      return getTopexStyle(feature, resolution, '');
    }
  });

  forestClassesLayerProperty = new VectorLayer({
    source: this.forestClassesVectorSource_Property,
    /* A function that returns a style object. */
    visible: false,
    style: (feature, resolution) => {
      return getFcCodeStyle(feature, resolution, '');
    }
  });

  forestClassesLayerPaddock = new VectorLayer({
    source: this.forestClassesVectorSource_Paddock,
    /* A function that returns a style object. */
    visible: false,
    style: (feature, resolution) => {
      return getFcCodeStyle(feature, resolution, '');
    }
  });

  cartageLayerProperty = new VectorLayer({
    source: this.cartageVectorSource_Property,
    /* A function that returns a style object. */
    visible: false,
    style: (feature, resolution) => {
      return getFcCodeStyle(feature, resolution, '');
    }
  });

  cartageLayerPaddock = new VectorLayer({
    source: this.cartageVectorSource_Paddock,
    /* A function that returns a style object. */
    visible: false,
    style: (feature, resolution) => {
      return getFcCodeStyle(feature, resolution, '');
    }
  });

  subregionLayerProperty = new VectorLayer({
    source: this.subregionVectorSource_Property,
    /* A function that returns a style object. */
    visible: false,
    style: (feature, resolution) => {
      return getFcCodeStyle(feature, resolution, '');
    }
  });

  subregionLayerPaddock = new VectorLayer({
    source: this.subregionVectorSource_Paddock,
    /* A function that returns a style object. */
    visible: false,
    style: (feature, resolution) => {
      return getFcCodeStyle(feature, resolution, '');
    }
  });

  fenceLayer = new VectorLayer({
    source: new VectorSource({}),
    style: (feature, resolution) => {
      return getFenceStyle(feature, resolution, '', this.map);
    },
    updateWhileAnimating: true,
    updateWhileInteracting: true
  });

  firePermitsLayer: VectorLayer<any> = new VectorLayer({
    source: new VectorSource(),
    style: styleMap.get('Fire Permits')
  });

  fireAlertsLayer: VectorLayer<any> = new VectorLayer({
    source: new VectorSource(),
    style: styleMap.get('Fire Alerts')
  });

  fireBurntAreaLayer: VectorLayer<any> = new VectorLayer({
    source: new VectorSource(),
    style: styleMap.get('Fire Permits')
  });

  enterpriseSuitibilityGroupProperty: LayerGroup = new LayerGroup({
    title: 'Analysis Output - Property',
    fold: 'open'
  } as any);

  enterpriseSuitibilityGroupPaddock: LayerGroup = new LayerGroup({
    title: 'Analysis Output - Paddock',
    fold: 'open'
  } as any);

  propertyLayerGroup: LayerGroup = new LayerGroup({
    title: 'Property Features',
    fold: 'open',
    layers: [this.propertyLayer, this.paddockLayer, this.fenceLayer]
  } as any);

  alertsGroup: LayerGroup = new LayerGroup({
    title: 'Alerts',
    fold: 'open',
    layers: [
      this.firePermitsLayer,
      this.fireAlertsLayer,
      this.fireBurntAreaLayer
    ]
  } as any);

  clippedLayerGroup: LayerGroup = new LayerGroup({
    title: 'Reference Layers',
    fold: 'open'
  } as any);

  clippedLayerMap: Map<string, VectorLayer<any>> = new Map();

  versatilityVectorSource_Property: VectorSource<any> = new VectorSource();
  versatilityVectorSource_Paddock: VectorSource<any> = new VectorSource();

  private _createPropertyToolActive$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);

  clippedLayers: Collection<any> = new Collection();

  paddocks: Array<Feature<any>> = [];

  eskTools: Array<any> = [];

  user: User;
  activeProperty: PropertyDexie;
  activePropertyFeature: Feature<any>;

  paddockFeatureMap: Map<string, Feature<any>> = new Map();
  fenceFeatureMap: Map<string, Feature<any>> = new Map();
  hasActiveProperty: boolean = false;
  sidebar: SidebarEsk;

  enterprises: Array<Enterprise> = [];

  constructor() {
    this.propertyLayer.set('title', 'Boundary');
    this.paddockLayer.set('title', 'Paddocks');
    this.fenceLayer.set('title', 'Fences');
    this.topexLayerProperty.set('title', 'Topex Property');
    this.topexLayerPaddock.set('title', 'Topex Paddock');
    this.forestClassesLayerProperty.set('title', 'Forest Classes Property');
    this.forestClassesLayerPaddock.set('title', 'Forest Classes Paddock');
    this.cartageLayerProperty.set('title', 'Cartage Property');
    this.cartageLayerPaddock.set('title', 'Cartage Paddock');
    this.subregionLayerProperty.set('title', 'Subregion Property');
    this.subregionLayerPaddock.set('title', 'Subregion Paddock');
    this.firePermitsLayer.set('title', 'Fire Permits');
    this.firePermitsLayer.set(
      'iconStyle',
      firePermitStyleIcon.getImage(2).toDataURL()
    );
    this.fireAlertsLayer.set('title', 'Current Fire Alerts (with details)');
    this.fireAlertsLayer.set(
      'iconStyle',
      fireAlertStyleIcon.getImage(2).toDataURL()
    );
    this.fireBurntAreaLayer.set(
      'title',
      'Current Fire Burnt Area Polygons (with details)'
    );

    this.alertsGroup.setVisible(false);

    LIST_MAP_DEFS.forEach((def) => {
      let lyr = new VectorLayer({
        source: new VectorSource(),
        style: styleMap.get(def.name)
      });

      lyr.set('title', def.name);

      this.clippedLayerMap.set(def.name, lyr);
      this.clippedLayers.push(lyr);
    });

    this.clippedLayerGroup.setLayers(this.clippedLayers);

    this.fenceService.fences.subscribe((fences) => {
      this.loadFences(fences);
    });

    this.basemapGroup = new LayerGroup({
      layers: [],
      title: 'Background Maps',
      fold: 'open'
    } as any);

    VERSATILITY_NAMES.forEach((crop) => {
      let layer = new VectorLayer({
        source: this.versatilityVectorSource_Property,
        style: (feature, resolution) => {
          return getVersatilityStyle(feature, resolution, crop.name);
        },
        visible: false
      });

      layer.set('title', crop.name);
      layer.set('label', crop.label);

      this.enterpriseSuitibilityGroupProperty.getLayers().push(layer);
    });

    VERSATILITY_NAMES.forEach((crop) => {
      let layer = new VectorLayer({
        source: this.versatilityVectorSource_Paddock,
        style: (feature, resolution) => {
          return getVersatilityStyle(feature, resolution, crop.name);
        },
        visible: false
      });

      layer.set('title', crop.name);
      layer.set('label', crop.label);

      this.enterpriseSuitibilityGroupPaddock.getLayers().push(layer);
    });

    this.zone.runOutsideAngular(() => {
      this.map = new ExportableMap({
        layers: [
          this.basemapGroup,

          this.alertsGroup,
          this.clippedLayerGroup,
          this.propertyLayerGroup,
          this.enterpriseSuitibilityGroupPaddock,
          this.enterpriseSuitibilityGroupProperty,
          new LayerGroup({
            layers: [
              this.topexLayerProperty,
              this.topexLayerPaddock,
              this.forestClassesLayerPaddock,
              this.forestClassesLayerProperty,
              this.cartageLayerPaddock,
              this.cartageLayerProperty,
              this.subregionLayerPaddock,
              this.subregionLayerProperty,
              this.propertiesLayer
            ]
          })
        ],
        view: new View({
          center: [494095, 5343594],
          projection: this.proj,
          zoom: 5,
          maxZoom: 23
        }),
        pixelRatio: window.devicePixelRatio,
        maxTilesLoading: 256,
        controls: [new Attribution()]
      });

      this.enterpriseSuitibilityGroupProperty.setVisible(false);
      this.enterpriseSuitibilityGroupPaddock.setVisible(false);

      this.enterpriseSuitibilityGroupPaddock.on('change:visible', (event) => {
        if (this.enterpriseSuitibilityGroupPaddock.getVisible()) {
          this.enterpriseSuitibilityGroupProperty.setVisible(false);
        }
      });

      this.enterpriseSuitibilityGroupProperty.on('change:visible', (event) => {
        if (this.enterpriseSuitibilityGroupProperty.getVisible()) {
          this.enterpriseSuitibilityGroupPaddock.setVisible(false);
        }
      });
    });

    this.propertyService.activeProperty
      .pipe(
        tap((property) => {
          if (property == null) {
            this.clearLayers();
            this.propertyLayerGroup
              .getLayers()
              .forEach((layer: VectorLayer<any>) => {
                layer.getSource().clear();
              });
            this.propertyLayer.getSource().clear();
            this.hasActiveProperty = false;
            console.log(this.propertyLayer.getSource().getFeatures());
            return;
          }
          this.clearLayers();
          this.activeProperty = property;
          this.activePropertyFeature = this.addProperty(property.wkt);
          this.zoomToActiveProperty();
          this.hasActiveProperty = true;
        }),
        switchMap(
          (
            property
          ):
            | Observable<Array<ClippedPropertyLayer>>
            | Observable<Array<any>> => {
            return property == null
              ? of([])
              : this.baseLayerService.getClippedLayers_Property(
                  property.propertyID
                );
          }
        )
      )
      .subscribe(async (clippedLayers) => {
        this.loadClippedPropertyLayers(clippedLayers);
      });

    this.propertyService.properties.subscribe((properties: PropertyDexie[]) => {
      this.propertiesLayer.getSource().clear();
      properties.forEach((property) => {
        const _feature = ConvertWktToFeature(property.wkt);
        _feature.set('propertyID', property.propertyID);
        _feature.set('name', property.name);
        this.propertiesVectorSource.addFeature(_feature);
      });
    });

    this.paddockService.paddocks.subscribe(async (paddocks) => {
      this.paddockLayer.getSource().clear();
      this.versatilityVectorSource_Paddock.clear();
      this.paddockFeatureMap.clear();

      if (paddocks == null) {
        return;
      }

      for (let index = 0; index < paddocks.length; index++) {
        const paddock = paddocks[index];

        let feature = ConvertWktToFeature(paddock.wkt);

        for (const property in paddock) {
          if (
            (property != 'geom' && property != 'wkt') ||
            typeof paddock[property] !== 'object'
          ) {
            feature.set(property, paddock[property]);
          }
        }

        this.paddockFeatureMap.set(paddock.paddockID, feature);
        this.paddockLayer.getSource().addFeature(feature);
      }
    });

    this.auth.user.subscribe((user) => {
      this.user = user;
    });

    this.enterpriseService.enterprises.subscribe(
      (data) => (this.enterprises = data)
    );
  }

  get createPropertyToolActive(): Observable<boolean> {
    return this._createPropertyToolActive$.pipe(
      tap((active) => {
        if (!active) {
          this.propertiesLayer.setVisible(false);
          return;
        }

        this.propertiesLayer.setVisible(true);
      })
    );
  }

  setCreatePropertyToolActive(value: boolean) {
    this._createPropertyToolActive$.next(value);
  }

  async downloadClippedLayers(paddocks: Array<PaddockDexie>) {
    this.ProcessingState.next(ProcessState.Clipping_Layers);

    await this.processProperty(
      ConvertWktToFeature(this.activeProperty.wkt),
      this.activeProperty
    );

    let propertyClippedLayers =
      await this.baseLayerService.getClippedLayers_Property_Async(
        this.activeProperty.propertyID
      );

    this.loadClippedPropertyLayers(propertyClippedLayers);

    for (let index = 0; index < paddocks.length; index++) {
      const paddock = paddocks[index];
      const feature = this.paddockFeatureMap.get(paddock.paddockID);
      await this.getPaddockVersatilityLayer(feature, paddock);
      await this.getPaddockTopexLayer(feature, paddock);

      let clippedLayers = await this.baseLayerService.getClippedLayers_Paddock(
        paddock.paddockID
      );
      this.loadClippedPaddockLayers(clippedLayers);
    }

    this.ProcessingState.next(ProcessState.Not_Processing);
  }

  togglePropertyStyle(edit: boolean) {
    if (edit) {
      this.propertyLayer.setStyle(styleMap.get('editProperty'));
    } else {
      this.propertyLayer.setStyle(styleMap.get('Property'));
    }
  }

  async loadSelectedPaddocks(paddocks: Array<PaddockDexie>) {
    this.versatilityVectorSource_Paddock.clear();
    this.topexVectorSource_Paddock.clear();
    this.forestClassesVectorSource_Paddock.clear();
    this.cartageVectorSource_Paddock.clear();
    this.subregionVectorSource_Paddock.clear();

    for (let index = 0; index < paddocks.length; index++) {
      const paddock = paddocks[index];
      let clippedLayers = await this.baseLayerService.getClippedLayers_Paddock(
        paddock.paddockID
      );
      this.loadClippedPaddockLayers(clippedLayers);
    }

    this.map.render();
  }

  /**
   * Sets the view to the accordant zoom and center.
   *
   * @param zoom Zoom.
   * @param center Center in long/lat.
   */
  setView(zoom: number, center: [number, number]) {
    //this.map.getView().setZoom(10);
  }

  zoomToPaddock(paddock: PaddockDexie) {
    this.paddockFeatureMap.has(paddock.paddockID);
    {
      let feature = this.paddockFeatureMap.get(paddock.paddockID);
      this.zoomToFeature(feature);
    }
  }

  async loadFences(fences: FenceDexie[]) {
    this.fenceFeatureMap.clear();
    this.fenceLayer.getSource().clear();

    fences.forEach((fence) => {
      let feature = ConvertWktToFeature(fence.wkt);
      feature.set('existing', fence.existing);
      feature.set('wallabyProof', fence.wallabyProof);
      feature.set('shape_length', fence.length);
      feature.set('FEAT_NAME', fence.name);
      this.fenceFeatureMap.set(fence.fenceID, feature);
      this.fenceLayer.getSource().addFeature(feature);
    });
  }

  setActiveProperty(property: PropertyDexie) {
    this.propertyService.activeProperty.next(property);
  }

  zoomToFence(fence: FenceDexie) {
    this.fenceFeatureMap.has(fence.fenceID);
    {
      let feature = this.fenceFeatureMap.get(fence.fenceID);
      this.zoomToFeature(feature);
    }
  }

  /**
   * Updates target and size of the map.
   *
   * @param target HTML container.
   */
  updateSize(target = 'map') {
    this.map.setTarget(target);
    this.map.updateSize();
  }

  addDefaultControls(target = 'ol-toolbar') {
    //this.map.addControl(EskZoomTool(target));
  }

  addDefaultInteractions() {
    this.map.addInteraction(new PinchZoom());
    let _defaultInteractions = defaultInteractions();
    _defaultInteractions.forEach((interaction) => {
      this.map.addInteraction(interaction);
    });
  }

  addSideBar(target = 'sidebar_div') {
    this.zone.runOutsideAngular(() => {
      this.sidebar = new Sidebar({ target: target, position: 'right' });
      this.sidebar.setOpenCallback(this.sidebarToggleOpen);
      this.sidebar.setCloseCallback(this.sidebarToggleClose);
      this.map.addControl(this.sidebar);
    });

    return this.sidebar;
  }

  async CreatePaddock(feature: Feature<any>) {
    let paddock = new PaddockDexie();

    let ClippedPaddock = await Intersect(feature, this.activePropertyFeature);

    let paddockFeatures = this.paddockLayer.getSource().getFeatures();

    for (let index = 0; index < paddockFeatures.length; index++) {
      const PADDOCK_FEATURE = paddockFeatures[index];

      ClippedPaddock = await Difference(ClippedPaddock, PADDOCK_FEATURE);
    }

    feature.setGeometry(ClippedPaddock.getGeometry());
    let area = CalculateArea(feature);

    paddock.name = `Paddock ${this.paddockFeatureMap.size + 1}`;
    paddock.propertyID = this.activeProperty.propertyID;
    paddock.wkt = ConvertFeatureToWKT(feature);
    paddock.area = Math.round((area + Number.EPSILON) * 100) / 100;

    return paddock;
    // this.eventBusService.emit(new EventData(TreeMapperCustomEvents.PaddockFeatureCreated, paddock));
  }

  async getIntersectedPaddocks(feature: Feature<any>): Promise<Array<string>> {
    let intersectedPaddockIds = [];

    this.paddockFeatureMap.forEach((value, key, map) => {
      let intersects = Intersects(feature, value);
      if (intersects) {
        intersectedPaddockIds.push(key);
      }
    });

    return intersectedPaddockIds;
  }

  async createFence(fence: FenceDexie) {
    await this.fenceService.save(fence);
  }

  zoomToActiveProperty() {
    this.zoomToFeature(this.activePropertyFeature);
  }

  async updateFence(fence: FenceDexie) {
    this.fenceService.update(fence);
  }

  async deleteFence(fence: FenceDexie) {
    this.fenceService.remove(fence);
  }

  async createPaddock(paddock: PaddockDexie) {
    this.ProcessingState.next(ProcessState.Creating_Paddock);
    let feature = ConvertWktToFeature(paddock.wkt);
    await this.paddockService.save(paddock);
    this.ProcessingState.next(ProcessState.Clipping_Layers);
    this.ProgressDetail.next('Suitability Index');
    await this.getPaddockVersatilityLayer(feature, paddock);
    await this.getPaddockTopexLayer(feature, paddock);
    await this.getPaddockForestDescriptionLayer(feature, paddock);
    await this.getPaddockCartageLayer(feature, paddock);
    await this.getPaddockSubregionLayer(feature, paddock);

    let _clippedLayers = await this.baseLayerService.getClippedLayers_Paddock(
      paddock.paddockID
    );

    let topexFeaturesGeoJson = _clippedLayers.find(
      (layer) => layer.layerName == 'Topex'
    ).geom;
    let versatilityFeaturesGeoJson = _clippedLayers.find(
      (layer) => layer.layerName == 'Versatility'
    ).geom;

    let forestClassesGeoJson = _clippedLayers.find(
      (layer) => layer.layerName == 'ForestClasses'
    ).geom;

    let cartageGeoJson = _clippedLayers.find(
      (layer) => layer.layerName == 'Cartage'
    ).geom;

    let subregionGeoJson = _clippedLayers.find(
      (layer) => layer.layerName == 'Subregion'
    ).geom;

    this.loadClippedPaddockLayers(_clippedLayers);

    this.ProcessingState.next(ProcessState.Generating_Report);

    let topexReport = await this.generateTopexReport(topexFeaturesGeoJson);
    topexReport.paddockID = paddock.paddockID;

    let enterpriseReport = await this.generateSuitabilityReport(
      versatilityFeaturesGeoJson
    );

    enterpriseReport.paddockID = paddock.paddockID;

    let forestClassReport = await this.generateForestClassesReport(
      forestClassesGeoJson
    );

    forestClassReport.paddockID = paddock.paddockID;

    let peatReport = await this.generatePeatReport(
      feature,
      paddock.area,
      cartageGeoJson,
      subregionGeoJson,
      enterpriseReport
    );

    peatReport.paddockID = paddock.paddockID;

    await this.forestDescriptionReportService.save(forestClassReport);

    await this.enterpriseReportService.save(enterpriseReport);

    await this.topexReportService.save(topexReport);

    await this.pineEconomicAnalysisReportService.saveReport(peatReport);

    await this.paddockService.update(paddock);
    this.ProcessingState.next(ProcessState.Not_Processing);
  }

  async generateSuitabilityReport(features: string): Promise<EnterpriseReport> {
    return new Promise(async (resolve, reject) => {
      let report = new EnterpriseReport();

      let reportData = await this.generateSuitabilityReportData(features);

      reportData.sort(function (a, b) {
        return b.cropRankValue - a.cropRankValue;
      });

      let ranks = rankCrops(reportData);
      reportData.forEach((data, index) => {
        data.rank = ranks[index];
      });

      report.data = reportData;

      resolve(report);
    });
  }

  async generateSuitabilityReportData(
    features: string
  ): Promise<Array<EnterpriseReportRow>> {
    return new Promise(async (resolve, reject) => {
      if (typeof Worker !== 'undefined') {
        // Create a new
        const worker = new Worker(
          new URL('../workers/reportGenerator.worker.ts', import.meta.url)
        );
        worker.onmessage = ({ data }) => {
          resolve(data);
          worker.terminate();
        };

        worker.postMessage({
          features: features,
          enterprises: this.enterprises
        });
      } else {
        reject('WORKER_NOT_SUPPORTED');
      }
    });
  }

  async generateTopexReport(features: String): Promise<TopexReport> {
    return new Promise(async (resolve, reject) => {
      if (typeof Worker !== 'undefined') {
        // Create a new
        const worker = new Worker(
          new URL('../workers/topexReportGenerator.worker', import.meta.url)
        );
        worker.onmessage = ({ data }) => {
          resolve(data);
          worker.terminate();
        };

        worker.postMessage({ features: features });
      } else {
        reject('WORKER_NOT_SUPPORTED');
      }
    });
  }

  async generateForestClassesReport(
    features: String
  ): Promise<ForestDescriptionReport> {
    return new Promise(async (resolve, reject) => {
      if (typeof Worker !== 'undefined') {
        // Create a new
        const worker = new Worker(
          new URL(
            '../workers/forestClassesReportGenerator.worker',
            import.meta.url
          )
        );
        worker.onmessage = ({ data }) => {
          resolve(data);
          worker.terminate();
        };

        worker.postMessage({ features: features });
      } else {
        reject('WORKER_NOT_SUPPORTED');
      }
    });
  }

  async generatePeatReport(
    feature: Feature<any>,
    area: number,
    cartageFeatures: string,
    subregionGeoJson: string,
    enterpriseReport: EnterpriseReport
  ): Promise<PineEconomicAnalysisReport> {
    return new Promise(async (resolve, reject) => {
      const latlon = this._getFeatureCenterPoint(feature);
      let accuYieldRows: PineEconomicAnalysisToolAccuYieldRow[];
      if (latlon.length > 0) {
        const response =
          await this.pineEconomicAnalysisReportService.getAccuData(latlon);
        accuYieldRows =
          this.pineEconomicAnalysisReportService.processACCUData(response);
      }
      const cartage: PeatLayerDTO = JSON.parse(cartageFeatures);
      const subregion: PeatLayerDTO = JSON.parse(subregionGeoJson);

      const regimes = ['TH', 'UT'];

      const harvestProducts = [
        {
          product: 'Large Sawlogs',
          key: 'largeSawlogs',
          millField: 'closest_mill_large_sawlog',
          distanceField: 'cartage_distance_large_sawlog_km'
        },
        {
          product: 'Small Sawlogs',
          key: 'smallSawlogs',
          millField: 'closest_mill_small_sawlog',
          distanceField: 'cartage_distance_small_sawlog_km'
        },
        {
          product: 'Posts',
          key: 'posts',
          millField: 'closest_mill_posts',
          distanceField: 'cartage_distance_posts_km'
        },
        {
          product: 'Pulplogs',
          key: 'pulplogs',
          millField: 'closest_mill_pulplog',
          distanceField: 'cartage_distance_pulplog_km'
        }
      ];

      const largestArea = cartage.features.reduce((max, current) =>
        current.properties.shape_area > max.properties.shape_area
          ? current
          : max
      );

      const { wellSuited, suitable, moderatelySuitable, unsuitable } =
        enterpriseReport.data.find((d) => d.crop.name === 'pradiata');

      const subregionProps = subregion.features[0]
        .properties as SubregionPropertiesDTO;

      let siteQuality: number;

      if (wellSuited > suitable + moderatelySuitable + unsuitable) {
        siteQuality = Number.parseInt(subregionProps.sq_class_priority);
      } else if (suitable + moderatelySuitable > unsuitable) {
        siteQuality = Number.parseInt(subregionProps.sq_class_marginal);
      } else {
        siteQuality = 7;
      }

      const { thinAge, harvestAge } =
        await this.pineEconomicAnalysisReportService.getThinningAndHarvestAgesBySiteCurve(
          siteQuality
        );

      const peatReport: PineEconomicAnalysisReport =
        new PineEconomicAnalysisReport(
          siteQuality,
          thinAge,
          harvestAge,
          area,
          wellSuited,
          suitable,
          moderatelySuitable,
          unsuitable,
          subregionProps.sub_name_7,
          latlon
        );

      const dummyThinningAndHarvest =
        ThinningAndHarvestYield.getDummyHarvestAndYield();

      const ageOfPlantation: number[] = Array.from({ length: 31 }, (_, i) => i);
      const ROMAN_NUMERALS = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];
      const harvestYieldRows = await Promise.all(
        regimes.map(async (r) => {
          const yieldKey = `RAD_${r}_${ROMAN_NUMERALS[siteQuality]}`;
          const thinningKey = `${yieldKey}_${thinAge}`;
          const thinningYield: ThinningAndHarvestYield =
            thinAge === -1
              ? dummyThinningAndHarvest
              : await this.pineEconomicAnalysisReportService.getThinningAndHarvestYieldByKey(
                  thinningKey
                );
          const harvestKey: string = `${yieldKey}_${harvestAge}`;
          const harvestYield: ThinningAndHarvestYield =
            thinAge === -1 && r === 'TH'
              ? dummyThinningAndHarvest
              : await this.pineEconomicAnalysisReportService.getThinningAndHarvestYieldByKey(
                  harvestKey
                );

          return {
            thinRegime: r,
            yieldKey: yieldKey,
            thinAge: peatReport.thinAge,
            harvestAge: peatReport.harvestAge,
            thinningYield: thinningYield,
            harvestYield: harvestYield
          };
        })
      );

      let analysisRows: Array<PineEconomicAnalysisToolRow>;

      analysisRows = harvestProducts.map((product) => ({
        product: product.product,
        nearestMill: largestArea.properties[product.millField],
        distance: largestArea.properties[product.distanceField],
        pineEconomicAnalysisHarvestYieldRows: harvestYieldRows.map((row) => ({
          thinRegime: row.thinRegime,
          yieldKey: row.yieldKey,
          thinAge: row.thinAge,
          harvestAge: row.harvestAge,
          harvestYieldAgeOfPlantation: ageOfPlantation.map((age) => {
            if (age === row.thinAge) {
              return row.thinningYield[product.key];
            } else if (age === row.harvestAge) {
              return row.harvestYield[product.key];
            }
            return 0;
          })
        }))
      }));

      peatReport.pineEconomicAnalysisRows = analysisRows;

      peatReport.pineEconomicAnalysisAccuYieldRows = accuYieldRows;

      resolve(peatReport);
    });
  }

  async showCrop(cropName: string, type: PaddockDexie | PropertyDexie) {
    if (type instanceof PaddockDexie) {
      this.enterpriseSuitibilityGroupPaddock.setVisible(true);

      let clippedLayers = await this.baseLayerService.getClippedLayers_Paddock(
        type.paddockID
      );
      this.versatilityVectorSource_Paddock.clear();
      this.loadClippedPaddockLayers(clippedLayers);

      this.zoomToPaddock(type);
      this.enterpriseSuitibilityGroupProperty
        .getLayers()
        .forEach(function (layer) {
          layer.setVisible(false);
        });
      const paddock = this.enterpriseSuitibilityGroupPaddock
        .getLayers()
        .getArray()
        .find((layer) => layer.get('title') === cropName);
      if (paddock) {
        paddock.setVisible(true);
      }
    } else if (type instanceof PropertyDexie) {
      this.enterpriseSuitibilityGroupProperty.setVisible(true);

      this.zoomToFeature(this.activePropertyFeature);
      this.enterpriseSuitibilityGroupPaddock.setVisible(false);
      const property = this.enterpriseSuitibilityGroupProperty
        .getLayers()
        .getArray()
        .find((layer) => layer.get('title') === cropName);
      if (property) {
        property.setVisible(true);
      }
    }
  }

  async getPaddockVersatilityLayer(
    feature: Feature<any>,
    paddock: PaddockDexie
  ) {
    let _features = this.versatilityVectorSource_Property.getFeatures();

    let featureArray: Array<Feature<any>> = [];

    for (let i = 0; i < _features.length; i++) {
      let _intersect = await Intersect(_features[i], feature);

      if (_intersect != null) {
        _intersect = CloneProperties(_features[i], _intersect);

        featureArray.push(_intersect);
      }
    }

    let _clippedLayer = new ClippedLayer_Paddock();
    _clippedLayer.paddockID = paddock.paddockID;
    _clippedLayer.layerName = 'Versatility';
    _clippedLayer.geom = ConvertFeatureCollectionToGeoJson(featureArray);

    await this.baseLayerService.addClippedLayers_Paddock(_clippedLayer);
  }

  async getPaddockTopexLayer(feature: Feature<any>, paddock: PaddockDexie) {
    let _features = this.topexVectorSource_Property.getFeatures();

    let featureArray: Array<Feature<any>> = [];

    for (let i = 0; i < _features.length; i++) {
      let _intersect = await Intersect(_features[i], feature);

      if (_intersect != null) {
        _intersect = CloneProperties(_features[i], _intersect);

        featureArray.push(_intersect);
      }
    }

    let _clippedLayer = new ClippedLayer_Paddock();
    _clippedLayer.paddockID = paddock.paddockID;
    _clippedLayer.layerName = 'Topex';
    _clippedLayer.geom = ConvertFeatureCollectionToGeoJson(featureArray);

    await this.baseLayerService.addClippedLayers_Paddock(_clippedLayer);
  }

  async getPaddockForestDescriptionLayer(
    feature: Feature<any>,
    paddock: PaddockDexie
  ) {
    let _features = this.forestClassesVectorSource_Property.getFeatures();

    let featureArray: Array<Feature<any>> = [];

    for (let i = 0; i < _features.length; i++) {
      let _intersect = await Intersect(_features[i], feature);

      if (_intersect != null) {
        _intersect = CloneProperties(_features[i], _intersect);

        featureArray.push(_intersect);
      }
    }

    let _clippedLayer = new ClippedLayer_Paddock();
    _clippedLayer.paddockID = paddock.paddockID;
    _clippedLayer.layerName = 'ForestClasses';
    _clippedLayer.geom = ConvertFeatureCollectionToGeoJson(featureArray);

    await this.baseLayerService.addClippedLayers_Paddock(_clippedLayer);
  }

  async getPaddockCartageLayer(feature: Feature<any>, paddock: PaddockDexie) {
    let _features = this.cartageVectorSource_Property.getFeatures();

    let featureArray: Array<Feature<any>> = [];

    for (let i = 0; i < _features.length; i++) {
      let _intersect = await Intersect(_features[i], feature);

      if (_intersect != null) {
        _intersect = CloneProperties(_features[i], _intersect);

        featureArray.push(_intersect);
      }
    }

    let _clippedLayer = new ClippedLayer_Paddock();
    _clippedLayer.paddockID = paddock.paddockID;
    _clippedLayer.layerName = 'Cartage';
    _clippedLayer.geom = ConvertFeatureCollectionToGeoJson(featureArray);

    await this.baseLayerService.addClippedLayers_Paddock(_clippedLayer);
  }

  async getPaddockSubregionLayer(feature: Feature<any>, paddock: PaddockDexie) {
    let _features = this.subregionVectorSource_Property.getFeatures();

    let featureArray: Array<Feature<any>> = [];

    for (let i = 0; i < _features.length; i++) {
      let _intersect = await Intersect(_features[i], feature);

      if (_intersect != null) {
        _intersect = CloneProperties(_features[i], _intersect);

        featureArray.push(_intersect);
      }
    }

    let _clippedLayer = new ClippedLayer_Paddock();
    _clippedLayer.paddockID = paddock.paddockID;
    _clippedLayer.layerName = 'Subregion';
    _clippedLayer.geom = ConvertFeatureCollectionToGeoJson(featureArray);

    await this.baseLayerService.addClippedLayers_Paddock(_clippedLayer);
  }

  unionFeatures(features: Array<Feature<any>>, options?) {
    let feature = Union(features);

    feature = Buffer(feature, { amount: options.bufferAmount });

    return feature;
  }

  convertToGeoJSON(feature: Feature<any>) {
    return ConvertFeatureToGeoJson(feature);
  }

  removeControls() {
    let interactions = this.map.getInteractions();

    let layers = this.map.getAllLayers();
    layers.forEach((layer) => {
      this.map.removeLayer(layer);
    });

    interactions.forEach((interaction) => {
      this.map.removeInteraction(interaction);
    });

    let controls = this.map.getControls();

    controls.forEach((control) => {
      this.map.removeControl(control);
    });

    this.eskTools = [];
  }

  addLayerSwitcher(target = 'layers') {
    // this.map.addControl(layerSwitcher);
  }

  sidebarToggleOpen(openID: string, closeID: string) {}

  sidebarToggleClose() {}

  async GetVersatilityLayer(feature: Feature<any>, property: PropertyDexie) {
    let features = await this.spatialService.getSuitabilityLayer(feature);

    let _clippedLayer = new ClippedPropertyLayer();
    _clippedLayer.propertyID = property.propertyID;
    _clippedLayer.layerName = 'Versatility';
    _clippedLayer.wkt = ConvertFeatureCollectionToGeoJson(features);

    return await this.baseLayerService.addClippedLayers_Property(_clippedLayer);
  }

  async GetTopexLayer(feature: Feature<any>, property: PropertyDexie) {
    let features = await this.spatialService.getTopexLayer(feature);

    let _clippedLayer = new ClippedPropertyLayer();
    _clippedLayer.propertyID = property.propertyID;
    _clippedLayer.layerName = 'Topex';
    _clippedLayer.wkt = ConvertFeatureCollectionToGeoJson(features);

    return await this.baseLayerService.addClippedLayers_Property(_clippedLayer);
  }

  async GetForestClassesLayer(feature: Feature<any>, property: PropertyDexie) {
    let features = await this.spatialService.getForestClassesLayer(feature);

    let _clippedLayer = new ClippedPropertyLayer();
    _clippedLayer.propertyID = property.propertyID;
    _clippedLayer.layerName = 'ForestClasses';
    _clippedLayer.wkt = ConvertFeatureCollectionToGeoJson(features);

    return await this.baseLayerService.addClippedLayers_Property(_clippedLayer);
  }

  async GetCartageLayer(feature: Feature<any>, property: PropertyDexie) {
    let features = await this.spatialService.getCartageLayer(feature);

    let _clippedLayer = new ClippedPropertyLayer();
    _clippedLayer.propertyID = property.propertyID;
    _clippedLayer.layerName = 'Cartage';
    _clippedLayer.wkt = ConvertFeatureCollectionToGeoJson(features);

    return await this.baseLayerService.addClippedLayers_Property(_clippedLayer);
  }

  async GetSubregionLayer(feature: Feature<any>, property: PropertyDexie) {
    let features = await this.spatialService.getSubregionLayer(feature);

    let _clippedLayer = new ClippedPropertyLayer();
    _clippedLayer.propertyID = property.propertyID;
    _clippedLayer.layerName = 'Subregion';
    _clippedLayer.wkt = ConvertFeatureCollectionToGeoJson(features);

    return await this.baseLayerService.addClippedLayers_Property(_clippedLayer);
  }

  async addTfsLayers() {
    let permitsResponse = await fetch(
      'https://tfsfeed.eskmapping.com.au/geoserver/tfsfeed/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=tfsfeed%3Apermits_detailed&outputFormat=application%2Fjson&srsname=EPSG:28355'
    );
    let permitsData = await permitsResponse.json();

    let permitFeatures = ConvertGeoJsonToFeatureCollection(permitsData);

    this.firePermitsLayer.getSource().addFeatures(permitFeatures);

    let fireBurntResponse = await fetch(
      'https://tfsfeed.eskmapping.com.au/geoserver/tfsfeed/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=tfsfeed%3Aburnt_area_detail&outputFormat=application%2Fjson&srsname=EPSG:28355'
    );
    let fireBurntData = await fireBurntResponse.json();

    let fireBurntFeatures = ConvertGeoJsonToFeatureCollection(fireBurntData);

    this.fireBurntAreaLayer.getSource().addFeatures(fireBurntFeatures);

    let fireAlertsResponse = await fetch(
      'https://tfsfeed.eskmapping.com.au/geoserver/tfsfeed/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=tfsfeed%3Aalert_summary_detailed&outputFormat=application%2Fjson&srsname=EPSG:28355'
    );
    let fireAlertsData = await fireAlertsResponse.json();

    let fireAlertsFeatures = ConvertGeoJsonToFeatureCollection(fireAlertsData);

    this.fireAlertsLayer.getSource().addFeatures(fireAlertsFeatures);
  }

  addCadastreLayer() {
    const serviceUrl =
      'https://services.thelist.tas.gov.au/arcgis/rest/services/Public/CadastreAndAdministrative/MapServer/';

    const layer = '38';

    const esrijsonFormat = new EsriJSON();

    const vectorSource = new VectorSource({
      loader: async (extent, resolution, projection, success, failure) => {
        let encodedComponent = encodeURIComponent(
          '{"xmin":' +
            extent[0] +
            ',"ymin":' +
            extent[1] +
            ',"xmax":' +
            extent[2] +
            ',"ymax":' +
            extent[3] +
            ',"spatialReference":{"wkid":28355}}'
        );

        const url = `${serviceUrl + layer}/query/?where=PID${encodeURIComponent(
          '>0'
        )}+AND+MEAS_AREA${encodeURIComponent(
          '>1000'
        )}+OR+COMP_AREA${encodeURIComponent(
          '>1000'
        )}+AND+PID${encodeURIComponent(
          '>0'
        )}&f=json&returnGeometry=true&spatialRel=esriSpatialRelIntersects&geometry=${encodedComponent}&geometryType=esriGeometryEnvelope&inSR=28355&outFields=*&outSR=28355`;

        try {
          let response = await fetch(url);

          if (response.ok) {
            let featuresJson = await response.json();

            for (const property in featuresJson.fieldAliases) {
              if (this.propAliasMap.has(property)) continue;

              this.propAliasMap.set(
                property,
                featuresJson.fieldAliases[property]
              );
            }

            const features = esrijsonFormat.readFeatures(featuresJson, {
              featureProjection: projection
            });
            if (features.length > 0) {
              features.forEach((element) => {
                let props = element.getProperties();
                element.setId(props.OBJECTID);
              });

              vectorSource.addFeatures(features);
            }

            success(features);
          }
        } catch (error) {
          failure();
        }
      },
      strategy: tileStrategy(
        createXYZ({
          tileSize: 1028
        })
      )
    });

    this.cadasterLayer = new VectorImageLayer({
      imageRatio: 2,
      source: vectorSource,
      style: (feature, resolution) => {
        return getCadasterStyle(feature, resolution, '');
      },
      minZoom: 9
    });

    this.cadasterLayer.set('title', 'Cadastre Land Parcles');
    this.cadasterLayer.setVisible(false);

    this.map.addLayer(this.cadasterLayer);

    this.rebuildLegend();
  }

  removeCadastreLayer() {
    this.map.removeLayer(this.cadasterLayer);
  }

  async createProperty(property: PropertyDexie, features: Array<Feature<any>>) {
    this.ProcessingState.next(ProcessState.Creating_Property);

    let feature = this.unionFeatures(features, {
      bufferAmount: 0
    });

    property.wkt = ConvertFeatureToWKT(feature);
    property.userID = this.user.Id;
    property.address = '';
    property.area = CalculateArea(feature);
    property.pid = property.pid.replace(',', '');

    await this.propertyService.add(property);

    await this.processProperty(feature, property);

    //this.activeProperty = property;
    //this.activePropertyFeature = this.addProperty(property.wkt);
    this.baseLayerService
      .getClippedLayers_Property(property.propertyID)
      .subscribe(async (clippedLayers) => {
        let topexFeaturesGeoJson = clippedLayers.find(
          (layer) => layer.layerName == 'Topex'
        ).wkt;
        let SuitabilityFeaturesGeoJson = clippedLayers.find(
          (layer) => layer.layerName == 'Versatility'
        ).wkt;

        let forestClassesGeoJson = clippedLayers.find(
          (layer) => layer.layerName == 'ForestClasses'
        ).wkt;

        let cartageGeoJson = clippedLayers.find(
          (layer) => layer.layerName == 'Cartage'
        ).wkt;

        let subregionGeoJson = clippedLayers.find(
          (layer) => layer.layerName == 'Subregion'
        ).wkt;

        // this.loadClippedPropertyLayers(clippedLayers);
        this.ProcessingState.next(ProcessState.Generating_Report);

        let enterpriseReport = await this.generateSuitabilityReport(
          SuitabilityFeaturesGeoJson
        );
        enterpriseReport.propertyID = property.propertyID;

        let topexReport = await this.generateTopexReport(topexFeaturesGeoJson);
        topexReport.propertyID = property.propertyID;

        let forestClassReport = await this.generateForestClassesReport(
          forestClassesGeoJson
        );

        let peatReport = await this.generatePeatReport(
          feature,
          property.area,
          cartageGeoJson,
          subregionGeoJson,
          enterpriseReport
        );

        peatReport.propertyID = property.propertyID;

        forestClassReport.propertyID = property.propertyID;

        await this.topexReportService.save(topexReport);
        await this.forestDescriptionReportService.save(forestClassReport);
        await this.enterpriseReportService.save(enterpriseReport);
        await this.pineEconomicAnalysisReportService.saveReport(peatReport);

        this.ProcessingState.next(ProcessState.Not_Processing);

        this.propertyService.activeProperty.next(property);
      });
  }

  async updateProperty(property: PropertyDexie) {
    this.propertyService.update(property);
  }

  async deleteProperty(property: PropertyDexie) {
    await this.propertyService.remove(property);
  }

  /**
   * It takes a feature, loops through a list of map definitions, fetches the data from the map
   * definition, intersects the fetched data with the feature, and then saves the intersected data to a
   * database.
   *
   * The problem is that the function is taking a long time to run.
   *
   * @param feature - Feature<any> - The feature that is being processed
   * @param {Property} property - This is the property that the user has selected.
   */
  private async processProperty(
    feature: Feature<any>,
    property: PropertyDexie
  ): Promise<void> {
    this.ProcessingState.next(ProcessState.Clipping_Layers);
    const clippedLayersToBeSaved: ClippedPropertyLayer[] = [];

    try {
      const geom = feature?.getGeometry?.();
      if (!geom) {
        this.notifyUser('No geometry found for this property. Skipping.');
        return;
      }

      const extent = geom.getExtent?.();
      if (!extent || extent.length !== 4) {
        this.notifyUser('Invalid geometry extent. Skipping.');
        return;
      }

      for (let index = 0; index < LIST_MAP_DEFS.length; index++) {
        const MAP_DEF = LIST_MAP_DEFS[index];
        this.ProgressDetail.next(MAP_DEF.name);

        try {
          const envelope = {
            xmin: extent[0],
            ymin: extent[1],
            xmax: extent[2],
            ymax: extent[3],
            spatialReference: { wkid: 28355 }
          };

          const geometryParam = encodeURIComponent(JSON.stringify(envelope));
          const API_URL =
            `${MAP_DEF.url}${MAP_DEF.id}/query/?` +
            `f=json&returnGeometry=true&spatialRel=esriSpatialRelIntersects&` +
            `geometry=${geometryParam}&geometryType=esriGeometryEnvelope&` +
            `inSR=28355&outFields=*&outSR=28355`;

          let featuresJson: any;
          try {
            featuresJson = await this.safeFetchJson(API_URL, {
              retries: 2,
              timeoutMs: 15000
            });
          } catch {
            this.notifyUser(`Failed to load ${MAP_DEF.name}. Skipped.`);
            continue;
          }

          try {
            this.updateAttributeMap(featuresJson);
          } catch {}

          let olFeatures: Feature<any>[];
          try {
            olFeatures = this.esrijsonFormat.readFeatures(featuresJson, {
              featureProjection: this.map.getView().getProjection()
            });
          } catch {
            this.notifyUser(`Could not parse ${MAP_DEF.name}. Skipped.`);
            continue;
          }

          if (!olFeatures?.length) continue;

          const featureArray: Feature<any>[] = [];
          for (const f of olFeatures) {
            try {
              const intersection = await Intersect(f, feature);
              if (!intersection) continue;
              let withProps = intersection;
              try {
                withProps = CloneProperties(f, intersection);
              } catch {}
              featureArray.push(withProps);
            } catch {}
          }

          if (!featureArray.length) continue;

          let geojsonStr = '[]';
          try {
            geojsonStr = ConvertFeatureCollectionToGeoJson(featureArray);
          } catch {
            this.notifyUser(`Failed to serialize ${MAP_DEF.name}. Skipped.`);
            continue;
          }

          const clipped = new ClippedPropertyLayer();
          clipped.propertyID = property.propertyID;
          clipped.layerName = MAP_DEF.name;
          clipped.wkt = geojsonStr;
          clippedLayersToBeSaved.push(clipped);
        } catch {}
      }

      try {
        if (clippedLayersToBeSaved.length) {
          await this.baseLayerService.addClippedLayers_Property(
            clippedLayersToBeSaved
          );
        }
      } catch {
        this.notifyUser(
          'Could not save some clipped layers. You can continue using the app.'
        );
      }

      await this.runStep('Versatility Index', () =>
        this.GetVersatilityLayer(feature, property)
      );
      await this.runStep('Topex Scores', () =>
        this.GetTopexLayer(feature, property)
      );
      await this.runStep('Forest Classes', () =>
        this.GetForestClassesLayer(feature, property)
      );
      await this.runStep('Cartage', () =>
        this.GetCartageLayer(feature, property)
      );
      await this.runStep('Subregion', () =>
        this.GetSubregionLayer(feature, property)
      );
    } catch {
      this.notifyUser('Processing failed. You can continue using the app.');
    } finally {
      this.ProgressDetail.next('');
      this.ProcessingState.next(ProcessState.Not_Processing);
    }
  }

  private async runStep(
    label: string,
    fn: () => Promise<unknown>
  ): Promise<void> {
    this.ProgressDetail.next(label);
    try {
      await fn();
    } catch {
      this.notifyUser(`Skipped: ${label}.`);
    }
  }

  private async safeFetchJson(
    url: string,
    opts: { retries?: number; timeoutMs?: number } = {}
  ): Promise<any> {
    const retries = Math.max(0, opts.retries ?? 0);
    const timeoutMs = Math.max(1, opts.timeoutMs ?? 15000);
    let attempt = 0;
    let lastErr: unknown;

    while (attempt <= retries) {
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const res = await fetch(url, { signal: controller.signal });
        clearTimeout(t);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return await res.json();
      } catch (err) {
        clearTimeout(t);
        lastErr = err;
        if (attempt < retries) {
          const backoffMs = Math.min(2000 * Math.pow(2, attempt), 10000);
          await this.sleep(backoffMs);
        }
      }
      attempt++;
    }
    throw lastErr ?? new Error('Request failed');
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private notifyUser(message: string): void {
    try {
      (this as any).notificationService?.error?.(message) ??
        (this as any).notificationService?.warn?.(message) ??
        this.ProgressDetail.next(message);
    } catch {
      this.ProgressDetail.next(message);
    }
  }

  /**
   * "If the property is not in the map, add it to the map."
   *
   * The function is called in the following code:
   * @param feature - The feature object that contains the attributes.
   */
  private updateAttributeMap(feature) {
    for (const property in feature.fieldAliases) {
      if (this.propAliasMap.has(property)) continue;

      this.propAliasMap.set(property, feature.fieldAliases[property]);
    }
  }

  /**
   * It loops through all the layers in the clippedLayerGroup and clears the source of each layer
   */
  clearLayers() {
    this.clippedLayerGroup.getLayersArray().forEach((lyr: VectorLayer<any>) => {
      lyr.getSource().clear();
    });

    this.versatilityVectorSource_Paddock.clear();
    this.versatilityVectorSource_Property.clear();

    this.topexVectorSource_Paddock.clear();
    this.topexVectorSource_Property.clear();

    this.forestClassesVectorSource_Paddock.clear();
    this.forestClassesVectorSource_Property.clear();

    this.cartageVectorSource_Paddock.clear();
    this.cartageVectorSource_Property.clear();

    this.subregionVectorSource_Paddock.clear();
    this.subregionVectorSource_Property.clear();
  }

  /**
   * It takes an array of clipped layers, converts each layer's geometry to a feature collection, and
   * adds the feature collection to the layer's source.
   *
   * The function is called from the following function: baseLayers.subscribe();
   * @param layers - Array<ClippedLayer>
   */
  loadClippedPropertyLayers(layers: Array<ClippedPropertyLayer>) {
    layers.forEach((layer) => {
      let source;
      if (layer.layerName == 'Versatility') {
        let _feature = ConvertGeoJsonToFeatureCollection(layer.wkt);
        source = this.versatilityVectorSource_Property;
        source.addFeatures(_feature);
      } else if (layer.layerName == 'Topex') {
        let _feature = ConvertGeoJsonToFeatureCollection(layer.wkt);
        source = this.topexVectorSource_Property;
        source.addFeatures(_feature);
      } else if (layer.layerName == 'ForestClasses') {
        let _feature = ConvertGeoJsonToFeatureCollection(layer.wkt);
        source = this.forestClassesVectorSource_Property;
        source.addFeatures(_feature);
      } else if (layer.layerName == 'Cartage') {
        let _feature = ConvertGeoJsonToFeatureCollection(layer.wkt);
        source = this.cartageVectorSource_Property;
        source.addFeatures(_feature);
      } else if (layer.layerName == 'Subregion') {
        let _feature = ConvertGeoJsonToFeatureCollection(layer.wkt);
        source = this.subregionVectorSource_Property;
        source.addFeatures(_feature);
      } else {
        let lyr = this.clippedLayerMap.get(layer.layerName);
        let _feature = ConvertGeoJsonToFeatureCollection(layer.wkt);
        source = lyr.getSource();
        source.addFeatures(_feature);
      }
    });
  }

  loadClippedPaddockLayers(layers: Array<any>) {
    layers.forEach((layer) => {
      let _feature = ConvertGeoJsonToFeatureCollection(layer.geom);
      let source;
      if (layer.layerName == 'Versatility') {
        source = this.versatilityVectorSource_Paddock;
      } else if (layer.layerName == 'Topex') {
        source = this.topexVectorSource_Paddock;
      } else if (layer.layerName == 'ForestClasses') {
        source = this.forestClassesVectorSource_Paddock;
      } else if (layer.layerName == 'Cartage') {
        source = this.cartageVectorSource_Paddock;
      } else if (layer.layerName == 'Subregion') {
        source = this.subregionVectorSource_Paddock;
      }

      source.addFeatures(_feature);
    });
  }

  /**
   * It takes a GeoJSON string or a Feature object and adds it to the map.
   * @param {string | Feature<any>} property - string | Feature<any>
   * @returns The feature that was added to the map.
   */
  addProperty(property: string | Feature<any>) {
    if (property == null) {
      return null;
    }

    let feature: Feature<any> = null;

    if (typeof property === 'string') {
      feature = ConvertWktToFeature(property);
    } else {
      feature = property;
    }

    this.propertyLayer.getSource().clear();

    this.propertyLayer.getSource().addFeature(feature);

    return feature;
  }

  /**
   * When the user clicks on a feature, zoom to the feature's geometry.
   * @param feature - Feature<any>
   */
  zoomToFeature(feature: Feature<any>) {
    if (feature == null) {
      return;
    }
    let polygon = feature.getGeometry();

    this.map.getView().fit(polygon, { padding: [150, 450, 150, 0] });
  }

  setupBaseMaps() {
    if (this.basemapGroup.values_.layers.array_.length == 0) {
      populateLayerGroup(base_map_defs, this.basemapGroup, this.rebuildLegend);
    } else return;
    //populateLayerGroup(defaultLayers, this.testGroup, this.rebuildLegend);
  }

  rebuildLegend() {
    // this.something.renderPanel();
    //this.layerSwitcher.renderPanel();
  }

  private _getFeatureCenterPoint(feature: Feature<any>): number[] {
    const mapProj = this.map.getView().getProjection();
    const geometry = feature.getGeometry();
    let coordinates: Coordinate;
    if (geometry instanceof MultiPolygon) {
      coordinates = geometry.getInteriorPoints().getFirstCoordinate();
    } else if (geometry instanceof Polygon) {
      coordinates = geometry.getInteriorPoint().getCoordinates();
    } else {
      return [];
    }

    const [lat, lon] = transform(coordinates, mapProj, 'EPSG:4326');

    return [lat, lon];
  }
}
